import type { StateTree } from '@makemydeal/dr-dash-types';
import * as offerReduxSelectors from './offerRedux';
import * as tradeInsSelectors from './tradeIns';
import * as vehicleSelectors from './vehicleSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

const getTradeValues = (state: StateTree) => {
    const summary = tradeInsSelectors.getTradeInSummary(state);
    return {
        tradeAcb: summary.totalAcv,
        netTrade: summary.netTrade,
        totalAllowance: summary.totalAllowance
    };
};

export const getFrontendProfitDetails = (state: StateTree) => {
    const profit = offerReduxSelectors.getSelectedPaymentProfitDetails(state);
    const retailPrice = offerReduxSelectors.getOfferPrice(state);
    const invoiceAmount = vehicleSelectors.getInvoiceAmount(state);
    const isVehicleCostInDealRecapEnabled = featureToggleSelectors.isEnableVehicleCostInDealRecap(state);
    const vehicleCost = isVehicleCostInDealRecapEnabled ? vehicleSelectors.getVehicleCost(state) : invoiceAmount || undefined;
    const { tradeAcb, netTrade, totalAllowance } = getTradeValues(state);
    const { frontEndProfit } = getGrossProfitDetails(state);
    const costReductionFromDealerCash =
        profit?.costReductionFromDealerCash ?? offerReduxSelectors.getAppliedIncentivesTotalForDealerCash(state);
    return {
        retailPrice,
        invoiceAmount,
        vehicleCost,
        ...(profit || {}),
        tradeAcb,
        netTrade,
        totalAllowance,
        costReductionFromDealerCash,
        frontEndProfit
    };
};

export const getBackendProfitDetails = (state: StateTree) => {
    const profit = offerReduxSelectors.getSelectedPaymentProfitDetails(state);
    const { backEndProfit } = getGrossProfitDetails(state);
    return {
        ...(profit && {
            addsToBackEndCost: profit.addsToBackEndCost,
            totalBackEndProtectionProductsPrice: profit.totalBackEndProtectionProductsPrice,
            totalBackEndProtectionProductsCost: profit.totalBackEndProtectionProductsCost,
            reserveProfit: profit.reserveProfit,
            reserve: {
                flatFee: profit.flatFee,
                reservePercentOfAmountFinanced: profit.reservePercentOfAmountFinanced,
                reservePaidOnPercentOfAmountFinanced: profit.reservePaidOnPercentOfAmountFinanced,
                reserveFromInterestParticipation: profit.reserveFromInterestParticipation,
                reserveParticipationPercentage: profit.reserveParticipationPercentage,
                rateAdjustmentRatio: profit.rateAdjustmentRatio,
                markupPercent: profit.markupPercent,
                reservePaid: profit.reservePaid
            }
        }),
        backEndProfit
    };
};

export const getGrossProfitDetails = (state: StateTree) => {
    const profit = offerReduxSelectors.getSelectedPaymentProfitDetails(state);
    const isUserProgramQuotes = offerReduxSelectors.getIsUserProgramQuotes(state);

    const frontEndProfit = profit?.frontEndProfit ?? 0;
    const backEndProfit = profit?.backEndProfit ?? 0;
    const reserveProfit = profit?.reserveProfit ?? 0;
    const totalProfit = profit?.totalProfit ?? 0;

    // Always combine reserve profit with back-end profit
    const backEndProfitValue = backEndProfit + reserveProfit;

    // Calculate total profit based on conditions
    const calculatedTotalProfit = isUserProgramQuotes ? frontEndProfit + backEndProfitValue : totalProfit;
    return {
        frontEndProfit,
        backEndProfit: backEndProfitValue,
        grossProfit: calculatedTotalProfit
    };
};
